import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { borderRadius, spacing } from '@tabbio/tokens';
import { NativeButton } from '@/components/common/NativeButton';
import { useTheme } from '@/hooks/useTheme';
import { useLocale } from '@/hooks/useLocale';
import { clampPhotoTransform, MAX_PHOTO_ZOOM, photoCropRectangle, photoDisplaySize, photoOutputSize, type PhotoTransform } from '@/lib/photo-crop-geometry';
import type { PhotoCropModalProps } from './photo-types';

export function PhotoCropModal({ source, kind, previewShape, onCancel, onConfirm }: PhotoCropModalProps) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const { isRTL } = useLocale();
  const round = (previewShape ?? (kind === 'account' ? 'round' : 'square')) === 'round';
  const titleId = useId();
  const instructionsId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const preview = useRef<HTMLImageElement>(null);
  const transform = useRef<PhotoTransform>({ zoom: 1, x: 0, y: 0 });
  const viewport = useRef(300);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ distance: number; zoom: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const saving = useRef(false);
  const alive = useRef(true);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const update = (next: PhotoTransform) => {
    transform.current = clampPhotoTransform(source, viewport.current, next);
    const size = photoDisplaySize(source, viewport.current, transform.current.zoom);
    const image = preview.current;
    if (image) {
      image.style.width = `${size.width}px`;
      image.style.height = `${size.height}px`;
      image.style.transform = `translate(${(viewport.current - size.width) / 2 + transform.current.x}px, ${(viewport.current - size.height) / 2 + transform.current.y}px)`;
    }
  };
  useEffect(() => {
    alive.current = true;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const current = dialog.current;
    current?.showModal();
    const cancel = (event: Event) => { event.preventDefault(); onCancelRef.current(); };
    current?.addEventListener('cancel', cancel);
    const resize = new ResizeObserver(() => {
      if (!frame.current) return;
      const previousViewport = viewport.current;
      viewport.current = frame.current.clientWidth;
      update({ ...transform.current, x: transform.current.x * viewport.current / previousViewport, y: transform.current.y * viewport.current / previousViewport });
    });
    if (frame.current) resize.observe(frame.current);
    return () => {
      alive.current = false;
      resize.disconnect();
      current?.removeEventListener('cancel', cancel);
      current?.close();
      previousFocus?.focus();
    };
  // The cropper is keyed by the source URI and resets for each selection.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeZoom = (value: number) => { update({ ...transform.current, zoom: value }); setZoom(transform.current.zoom); };
  const save = async () => {
    if (saving.current || !preview.current?.complete || !preview.current.naturalWidth) return;
    saving.current = true; setBusy(true); setError(false);
    try {
      const crop = photoCropRectangle(source, viewport.current, transform.current);
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = photoOutputSize(kind, crop.width);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas unavailable');
      context.drawImage(preview.current, crop.originX, crop.originY, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
      const png = source.asset.type === 'image/png';
      const type = png ? 'image/png' : 'image/jpeg';
      const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('Photo encoding failed')), type, 0.9));
      canvas.width = canvas.height = 0;
      if (alive.current) onConfirm({ uri: '', name: `tabbio-photo-${Date.now()}.${png ? 'png' : 'jpg'}`, type, size: blob.size, file: blob });
    } catch {
      if (alive.current) setError(true);
    } finally {
      saving.current = false;
      if (alive.current) setBusy(false);
    }
  };
  if (typeof document === 'undefined') return null;
  return createPortal(<dialog ref={dialog} data-photo-crop aria-labelledby={titleId} aria-describedby={instructionsId} dir={isRTL ? 'rtl' : 'ltr'}
    style={{ color: theme.text, background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: borderRadius['4xl'], padding: spacing[6], width: 'min(440px, calc(100vw - 32px))', maxHeight: 'calc(100dvh - 32px)', boxSizing: 'border-box', overflow: 'auto', fontFamily: 'inherit' }}>
    <style>{`dialog[data-photo-crop]::backdrop { background: rgba(0,0,0,.5); }`}</style>
    <div data-photo-crop-content style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacing[4] }}>
      <h2 id={titleId} style={{ margin: 0, fontSize: 22 }}>{t(kind === 'account' ? 'photoCrop.accountTitle' : 'photoCrop.cvTitle')}</h2>
      <p id={instructionsId} style={{ margin: 0, color: theme.textSecondary, textAlign: 'center' }}>{t('photoCrop.webInstructions')}</p>
      <div ref={frame} role="img" tabIndex={0} aria-label={t('photoCrop.preview')} aria-describedby={instructionsId}
        style={{ position: 'relative', direction: 'ltr', width: '100%', maxWidth: 360, aspectRatio: '1', overflow: 'hidden', borderRadius: round ? '50%' : borderRadius['2xl'], background: theme.surfaceSecondary, touchAction: 'none', cursor: busy ? 'wait' : 'grab', outlineOffset: 3 }}
        onKeyDown={event => {
          if (busy) return;
          const deltas: Record<string, [number, number]> = { ArrowLeft: [-10, 0], ArrowRight: [10, 0], ArrowUp: [0, -10], ArrowDown: [0, 10] };
          const delta = deltas[event.key];
          if (delta) { event.preventDefault(); update({ ...transform.current, x: transform.current.x + delta[0], y: transform.current.y + delta[1] }); }
          if (event.key === '+' || event.key === '-') { event.preventDefault(); changeZoom(transform.current.zoom + (event.key === '+' ? 0.1 : -0.1)); }
        }}
        onPointerDown={event => {
          if (busy) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (pointers.current.size === 2) {
            const [a, b] = [...pointers.current.values()];
            pinch.current = { distance: Math.hypot(a!.x - b!.x, a!.y - b!.y), zoom: transform.current.zoom };
          }
        }}
        onPointerMove={event => {
          if (busy) return;
          const previous = pointers.current.get(event.pointerId);
          if (!previous) return;
          pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (pointers.current.size === 2 && pinch.current) {
            const [a, b] = [...pointers.current.values()];
            const distance = Math.hypot(a!.x - b!.x, a!.y - b!.y);
            if (pinch.current.distance > 0) changeZoom(pinch.current.zoom * distance / pinch.current.distance);
          } else update({ ...transform.current, x: transform.current.x + event.clientX - previous.x, y: transform.current.y + event.clientY - previous.y });
        }}
        onPointerUp={event => { pointers.current.delete(event.pointerId); pinch.current = null; }}
        onPointerCancel={event => { pointers.current.delete(event.pointerId); pinch.current = null; }}>
        <img ref={preview} src={source.asset.uri} alt="" draggable={false} onLoad={() => update(transform.current)} onError={() => setError(true)}
          style={{ position: 'absolute', insetInlineStart: 0, top: 0, maxWidth: 'none', pointerEvents: 'none', userSelect: 'none' }} />
      </div>
      <label style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: spacing[2] }}>
        {t('photoCrop.zoom')}
        <input type="range" min={1} max={MAX_PHOTO_ZOOM} step={0.01} value={zoom} disabled={busy} onChange={event => changeZoom(Number(event.target.value))} aria-label={t('photoCrop.zoom')} />
      </label>
      <NativeButton title={t('photoCrop.reset')} variant="ghost" disabled={busy} onPress={() => { update({ zoom: 1, x: 0, y: 0 }); setZoom(1); }} />
      {error && <p role="alert" style={{ margin: 0, color: theme.danger }}>{t('photoCrop.failed')}</p>}
      <div style={{ display: 'flex', gap: spacing[3], width: '100%' }}>
        <NativeButton title={t('photoCrop.cancel')} variant="soft" onPress={onCancel} style={{ flex: 1 }} />
        <NativeButton title={t('photoCrop.usePhoto')} loading={busy} onPress={() => { void save(); }} style={{ flex: 1 }} />
      </div>
    </div>
  </dialog>, document.body);
}
